import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import PeoplePicker from "components/Common/PeoplePicker/PeoplePicker";
import BackdropComponent from "components/Spinner/Spinner";

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

export default function TASupportModalContent({
  onClose,
  handleSave,
  loading,
  isEdit,
  initialData = {},
}) {
  const [formData, setFormData] = useState({
    primaryTAOracleId: null,
    backupTAOracleId: null,
    taTeamEmail: "",
  });

  const [errors, setErrors] = useState({
    primaryTAOracleId: false,
    backupTAOracleId: false,
    taTeamEmail: false,
  });

  const [componentKey, setComponentKey] = useState(0);
  const initialDataLoadedRef = useRef(false);

  useEffect(() => {
    if (!initialDataLoadedRef.current) {
      const newFormData = {
        primaryTAOracleId: initialData.primaryTAOracleId || null,
        backupTAOracleId: initialData.backupTAOracleId || null,
        taTeamEmail: initialData.taTeamEmail || "",
      };

      setFormData(newFormData);
      initialDataLoadedRef.current = true;
    }
  }, []);

  useEffect(() => {
    return () => {
      initialDataLoadedRef.current = false;
    };
  }, []);

  const validateEmail = (email) => {
    if (!email) return true;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const handlePrimaryTAChange = (employee) => {
    if (!employee) return;

    const oracleId = employee?.OracleId || null;

    setFormData((prev) => ({
      ...prev,
      primaryTAOracleId: oracleId,
    }));
    setErrors((prev) => ({ ...prev, primaryTAOracleId: false }));
  };

  const handleBackupTAChange = (employee) => {
    if (!employee) return;

    const oracleId = employee?.OracleId || null;

    setFormData((prev) => ({
      ...prev,
      backupTAOracleId: oracleId,
    }));
    setErrors((prev) => ({ ...prev, backupTAOracleId: false }));
  };

  const handleTATeamEmailChange = (event) => {
    const value = event.target.value;

    setFormData((prev) => ({
      ...prev,
      taTeamEmail: value,
    }));
    setErrors((prev) => ({
      ...prev,
      taTeamEmail: value && !validateEmail(value),
    }));
  };

  const handlePrimaryTAReset = () => {
    setFormData((prev) => ({
      ...prev,
      primaryTAOracleId: null,
    }));
  };

  const handleBackupTAReset = () => {
    setFormData((prev) => ({
      ...prev,
      backupTAOracleId: null,
    }));
  };

  const handleSubmit = () => {
    const newErrors = {
      primaryTAOracleId: !formData.primaryTAOracleId,
      backupTAOracleId: false,
      taTeamEmail: formData.taTeamEmail && !validateEmail(formData.taTeamEmail),
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    const saveData = {
      primaryTAOracleId: formData.primaryTAOracleId,
      backupTAOracleId: formData.backupTAOracleId,
      taTeamEmail: formData.taTeamEmail,
    };

    handleSave(saveData);
  };

  const handleReset = () => {
    const resetData = {
      primaryTAOracleId: initialData.primaryTAOracleId || null,
      backupTAOracleId: initialData.backupTAOracleId || null,
      taTeamEmail: initialData.taTeamEmail || "",
    };

    setFormData(resetData);
    setErrors({
      primaryTAOracleId: false,
      backupTAOracleId: false,
      taTeamEmail: false,
    });
    setComponentKey((prev) => prev + 1);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 600 }}>
      {loading && <BackdropComponent />}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            fontSize: 18,
            fontWeight: "bold",
            color: "#3F5765",
            letterSpacing: "0.10em",
          }}
        >
          TA Support
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography
            sx={{
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              fontWeight: "bold",
              color: "#3F5765",
              letterSpacing: "0.10em",
              fontSize: 12,
              mb: 1,
            }}
          >
            Primary TA <span style={{ color: "red" }}>*</span>
          </Typography>
          <PeoplePicker
            key={`primary-${componentKey}`}
            label="Search Employee"
            placeholder="Search by name, email, or Oracle ID"
            onChange={handlePrimaryTAChange}
            onReset={handlePrimaryTAReset}
            defaultValue={initialData.primaryTAOracleId}
            isError={false}
            disabled={!isEdit}
          />
          {errors.primaryTAOracleId && (
            <Typography
              color="error"
              variant="caption"
              sx={{ display: "block", mt: 0.5 }}
            >
              Please select an employee
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography
            sx={{
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              fontWeight: "bold",
              color: "#3F5765",
              letterSpacing: "0.10em",
              fontSize: 12,
              mb: 1,
            }}
          >
            Backup TA
          </Typography>
          <PeoplePicker
            key={`backup-${componentKey}`}
            label="Search Employee"
            placeholder="Search by name, email, or Oracle ID"
            onChange={handleBackupTAChange}
            onReset={handleBackupTAReset}
            defaultValue={initialData.backupTAOracleId}
            isError={false}
            disabled={!isEdit}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography
            sx={{
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              fontWeight: "bold",
              color: "#3F5765",
              letterSpacing: "0.10em",
              fontSize: 12,
              mb: 1,
            }}
          >
            TA Team Email
          </Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={formData.taTeamEmail}
            onChange={handleTATeamEmailChange}
            error={errors.taTeamEmail}
            helperText={errors.taTeamEmail ? "Please enter a valid email" : ""}
            placeholder="Enter TA team email"
            disabled={!isEdit}
          />
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}
          >
            {isEdit && (
              <>
                <CustomOutlinedButton
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ minWidth: 85 }}
                >
                  Reset
                </CustomOutlinedButton>
                <CustomButton
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{ minWidth: 85 }}
                >
                  Save
                </CustomButton>
              </>
            )}
            {!isEdit && (
              <CustomOutlinedButton
                variant="outlined"
                onClick={onClose}
                sx={{ minWidth: 85 }}
              >
                Close
              </CustomOutlinedButton>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

TASupportModalContent.propTypes = {
  onClose: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  isEdit: PropTypes.bool.isRequired,
  initialData: PropTypes.object,
};
